// src/pages/AudiencesPage.js
import React, { useEffect } from 'react';
import Layout from '../components/Layout';
import styles from './AudiencesPage.module.css';
import ProductLogo from '../components/ProductLogo';
import Button from '../components/Button';
import CardContainer from '../components/CardContainer';
import StepCard from '../components/StepCard';
import { sendMessageToParent } from '../utils/postMessage';
import { useSearchParams } from 'react-router-dom';
import AudienceResult from '../components/AudienceResult';

const AudiencesPage = () => {
  useEffect(() => {
    document.title = 'Instant Audiences - Build Your Perfect Audience';
  }, []);

  const [searchParams] = useSearchParams();
  
  // Get values from URL params with defaults
  // const conversionStat = searchParams.get('conversion') || 'Up to 10x';
  const revenueStat = searchParams.get('revenue') || '$300K+';
  const roiStat = searchParams.get('roi') || '40x ROI';

  const handleActivateClick = () => {
    sendMessageToParent('ACTIVATE_FEATURE', {
      feature: 'audiences',
      redirect: '/audiences/onboarding/get-started',
      source: 'audiences_landing_page'
    });
  };

  const handleNavigateClick = (url='https://www.instant.one/case-studies') => {
         sendMessageToParent('NAVIGATE_TO', {
           feature: 'ai',
           redirect: url,
           source: 'ai_landing_page'
         });
       };

  return (
    <Layout>
      <div className={`page ${styles.audiencesPage}`}>
        <section className={styles.heroSection}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={`${styles.heroText} half-width`}>
                <div className={styles.iconProductName}>
                  <ProductLogo product={'attribution'} size={32} />
                  <span className={styles.productName}>Instant AUDIENCES</span>
                </div>
                <div className={styles.heroTitleContainer}>
                  <h1 className={styles.heroTitle}>
                    Convert more anonymous visitors into engaged customers
                  </h1>
                  <p className={styles.heroSubtitle}>
                    Don't leave potential on the table — Instant Audiences helps you identify even more anonymous shoppers, turning traffic into consistent revenue
                  </p>
                </div>
                <Button onClick={handleActivateClick}> Activate Instant Audiences </Button>
              </div>
              <div className={`${styles.heroImage} half-width`}>
                <video
                  src="/images/audiencesSolution.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  alt="Attribution Analytics Preview"
                />
              </div>
            </div>
          </div>
        </section>

        <hr />

        <section className={`${styles.container} ${styles.fullPotentialSection}`}>
          <div className={styles.headingContainer}>
            <h1 className={styles.sectionTitle}>Unlock the full potential of your traffic</h1>
            <p className={styles.sectionSubTitle}>Turn anonymous visits into revenue.</p>
          </div>
          <ul className={styles.cardContainer}>
            <li className='half-width'>
              <CardContainer className={styles.aiCard}>
                <h2 className={`${styles.cardHeading} green-text`}>Identify shoppers</h2>
                <p className={styles.cardStats}>Up to 10x</p>
                <p className={styles.cardDesc}>Multiply your conversions from the same traffic</p>
              </CardContainer>
            </li>

            <li className='half-width'>
              <CardContainer className={styles.aiCard}>
                <h2 className={`${styles.cardHeading} green-text`}>Boost revenue</h2>
                <p className={styles.cardStats}>{revenueStat}</p>
                <p className={styles.cardDesc}>Incremental revenue through smarter identification technology</p>
              </CardContainer>
            </li>

            <li className='half-width'>
              <CardContainer className={styles.aiCard}>
                <h2 className={`${styles.cardHeading} green-text`}>Amplify impact</h2>
                <p className={styles.cardStats}>{roiStat}</p>
                <p className={styles.cardDesc}>Serious returns, without increasing your ad spend</p>
              </CardContainer>
            </li>
          </ul>
        </section>

        <div className={styles.stickybar}>
            <div className={styles.barContentContainer}>
              <p>Identify anonymous visitors and convert them into loyal customers</p>
              <Button onClick={handleActivateClick}> Activate Instant Audiences </Button>
            </div>
        </div>

        <section className={`${styles.container} ${styles.howItWorksSection}`}>
          <div className={styles.headingContainer}>
            <h1 className={styles.sectionTitle}>How Instant Audiences works</h1>
            <p className={styles.sectionSubTitle}>Drive growth with unmatched shopper identification</p>
          </div>
          <ul className={styles.stepContainer}>
            <li className='half-width'>
              <StepCard stepNumber={1} icon={<img src="/images/icons/focus.webp" alt="Step 1 Icon" />} title="Real-time shopper identification event" description="Identify anonymous shoppers in real-time and capture their email addresses, enabling recognition at each site visit." />
            </li>

            <div className={styles.arrow}>
              <img src="/images/stepArrow.svg" alt="arrow" />
            </div>


            <li className='half-width'>
            <StepCard stepNumber={2} icon={<img src="/images/icons/EnvelopeOpen.webp" alt="Step 1 Icon" />} title="High-intent shopper is re-targeted" description="When a shopper shows high purchase intent, Audiences triggers flows to send an abandonment email." />
            </li>
            
            <div className={styles.arrow}>
              <img src="/images/stepArrow.svg" alt="arrow" />
            </div>


            <li className='half-width'>
            <StepCard stepNumber={3} icon={<img src="/images/icons/cartIcon.webp" alt="Step 1 Icon" />} title="Abandoned shopper is converted" description="Recover otherwise abandoned shoppers by bringing them back into the conversion funnel to complete their purchase." />
            </li>
          </ul>
        </section>

        <hr />

        <AudienceResult handleNavigateClick={handleNavigateClick} styles={styles}/>
      
      </div>
    </Layout>
  );
};

export default AudiencesPage;